import * as React from "react";
import { useState, useCallback, useEffect, useImperativeHandle, useMemo } from "react";
import styled from "styled-components";
import { Table, ControlLabel, Button, Modal } from "components/bootstrap";
import { PaginatedList, Spinner } from "components/common";
import ModalButtonToolbar from "components/common/ModalButtonToolbar";
import { FiltersRow } from "security-app/components/common";
import { GLCheckbox, RowTitleButton } from "common/components";
import { usePagination, useSetPagination, useSelectedRows, useSelectedRowsDispatch } from "common/contexts";
import useInvestigationPanel from "security-app/components/Investigations/hooks/useInvestigationPanel";
import {
  useGetInvestigations,
  useGetInvestigationDetails,
  useBulkAddEvidence
} from "security-app/hooks/useInvestigationsAPI";
import ListHeader from "./ListHeader";
import ListRows from "./ListRows";
import ListColGroup from "./ListColGroup";
const SelectedInvestigationsContainer = styled.div`
  margin-top: 1rem;
`;
const SelectedInvestigationsColumn = styled.div`
  margin: 0.8rem 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5rem;
`;
const List = ({
  type,
  ids,
  index = void 0,
  createdInvestigation = void 0,
  searchMeta = void 0,
  onSubmit: customOnSubmit = void 0,
  setIsSubmitting,
  setShowModal
}, ref) => {
  const archived = false;
  const localPagination = usePagination();
  const setLocalPagination = useSetPagination();
  const selectedInvestigations = useSelectedRows();
  const { selectedInvestigationId, openInvestigation } = useInvestigationPanel();
  const selectedInvestigationsDispatch = useSelectedRowsDispatch();
  const { currentInvestigation: activeInvestigation, loadingInvestigationDetails } = useGetInvestigationDetails(selectedInvestigationId);
  const selectedInvToShow = selectedInvestigations.length > 0 ? selectedInvestigations[0].id : null;
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const activeInvestigationId = activeInvestigation == null ? void 0 : activeInvestigation.id;
  const createdInvestigationId = createdInvestigation == null ? void 0 : createdInvestigation.id;
  useEffect(() => {
    if (createdInvestigation) {
      selectedInvestigationsDispatch({ type: "upsert", payload: [createdInvestigation] });
      return;
    }
    if (activeInvestigation && !activeInvestigation.archived) {
      selectedInvestigationsDispatch({ type: "add", payload: [activeInvestigation] });
    }
  }, [activeInvestigationId, createdInvestigationId, selectedInvestigationsDispatch]);
  const { bulkAddEvidence } = useBulkAddEvidence();
  const defaultOnSubmit = (payload) => bulkAddEvidence({ payload });
  const onSubmit = customOnSubmit != null ? customOnSubmit : defaultOnSubmit;
  const submitEvidence = useCallback(
    async (investigations2, evidenceIds) => {
      switch (type) {
        case "logs":
          await onSubmit({
            investigation_ids: investigations2.map((inv) => inv.id),
            logs: evidenceIds.map((id) => ({ id, index }))
          });
          break;
        case "searches":
        case "dashboards":
        case "events":
          await onSubmit({
            investigation_ids: investigations2.map((inv) => inv.id),
            [type]: evidenceIds
          });
          break;
        default:
          break;
      }
    },
    [index, onSubmit, type]
  );
  const showSelectedInvestigation = useCallback(() => {
    if (selectedInvToShow) {
      openInvestigation(selectedInvToShow);
    }
  }, [selectedInvToShow, openInvestigation]);
  const addEvidenceToSelectedInvestigations = useCallback(
    async (action = void 0) => {
      setIsSubmitting(true);
      let evidenceIds = ids;
      if (type === "searches") {
        const { searchTitle, isSearchRelative, saveAsAbsoluteSearch } = searchMeta;
        if (action === "absolute") {
          setShowConfirmationDialog(false);
          const newView = await saveAsAbsoluteSearch(`Absolute - ${searchTitle}`);
          evidenceIds = [newView.id];
        } else if (action === "relative") {
          setShowConfirmationDialog(false);
        } else if (action === "cancel") {
          setShowConfirmationDialog(false);
          setShowModal(false);
          return;
        } else if (isSearchRelative) {
          setShowConfirmationDialog(true);
          return;
        }
      }
      await submitEvidence(selectedInvestigations, evidenceIds);
      setIsSubmitting(false);
      setShowModal(false);
      showSelectedInvestigation();
    },
    [
      setIsSubmitting,
      ids,
      type,
      submitEvidence,
      selectedInvestigations,
      setShowModal,
      showSelectedInvestigation,
      searchMeta
    ]
  );
  useImperativeHandle(ref, () => ({
    onConfirm: () => addEvidenceToSelectedInvestigations(),
    onCancel: () => selectedInvestigationsDispatch({ type: "clear" }),
    getSelectedInvestigations: () => selectedInvestigations
  }));
  const investigationsQuery = [localPagination.query, activeInvestigationId ? `-id:${activeInvestigationId}` : ""].filter(Boolean).join(" ");
  const { investigations, pagination, loadingInvestigations } = useGetInvestigations(
    { ...localPagination, query: investigationsQuery },
    archived,
    void 0,
    { enabled: !loadingInvestigationDetails }
  );
  const onPageChange = (newPage, newPageSize) => {
    setLocalPagination({ ...localPagination, page: newPage, perPage: newPageSize });
  };
  const removeFilter = (filterKey) => (e) => {
    e.preventDefault();
    const auxFilters = { ...localPagination.filters };
    delete auxFilters[filterKey];
    setLocalPagination({ ...localPagination, filters: auxFilters });
  };
  const selectedInvOptions = useMemo(
    () => selectedInvestigations ? selectedInvestigations.map((inv) => ({ label: inv.name, value: inv.id })) : [],
    [selectedInvestigations]
  );
  const buttons = useMemo(
    () => /* @__PURE__ */ React.createElement(ModalButtonToolbar, null, /* @__PURE__ */ React.createElement(Button, { onClick: () => addEvidenceToSelectedInvestigations("cancel") }, "Cancel"), /* @__PURE__ */ React.createElement(Button, { onClick: () => addEvidenceToSelectedInvestigations("relative") }, "Save as Relative"), /* @__PURE__ */ React.createElement(Button, { bsStyle: "primary", onClick: () => addEvidenceToSelectedInvestigations("absolute") }, "Save as Absolute")),
    [addEvidenceToSelectedInvestigations]
  );
  const isActiveSelected = activeInvestigation ? !!selectedInvestigations.find((inv) => inv.id === activeInvestigation.id) : false;
  const onActiveChange = (e) => {
    selectedInvestigationsDispatch({ type: e.target.checked ? "add" : "remove", payload: [activeInvestigation] });
  };
  const onActiveNameClick = () => {
    selectedInvestigationsDispatch({ type: isActiveSelected ? "remove" : "add", payload: [activeInvestigation] });
  };
  const isLoading = loadingInvestigations || loadingInvestigationDetails;
  return /* @__PURE__ */ React.createElement(React.Fragment, null, activeInvestigation && !activeInvestigation.archived && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(ControlLabel, null, "Active investigation"), /* @__PURE__ */ React.createElement(Table, { condensed: true, "data-testid": "active-investigation-section" }, /* @__PURE__ */ React.createElement(ListColGroup, null), /* @__PURE__ */ React.createElement("tbody", null, /* @__PURE__ */ React.createElement("tr", null, /* @__PURE__ */ React.createElement("td", null, /* @__PURE__ */ React.createElement(
    GLCheckbox,
    {
      checked: isActiveSelected,
      onChange: onActiveChange,
      "data-testid": "active-investigation-checkbox"
    }
  )), /* @__PURE__ */ React.createElement("td", { "data-testid": "active-investigation-name", role: "checkbox", "aria-checked": isActiveSelected }, /* @__PURE__ */ React.createElement(RowTitleButton, { onClick: onActiveNameClick }, activeInvestigation.name)), /* @__PURE__ */ React.createElement("td", null, activeInvestigation.status))))), /* @__PURE__ */ React.createElement(
    PaginatedList,
    {
      activePage: localPagination.page,
      pageSize: localPagination.perPage,
      onChange: onPageChange,
      totalItems: pagination.total,
      useQueryParameter: false
    },
    /* @__PURE__ */ React.createElement(FiltersRow, { filters: localPagination.filters, onRemove: removeFilter }),
    /* @__PURE__ */ React.createElement(Table, { condensed: true, "data-testid": "open-investigations-list" }, !isLoading && /* @__PURE__ */ React.createElement(ListHeader, { investigations }), /* @__PURE__ */ React.createElement("tbody", null, isLoading ? /* @__PURE__ */ React.createElement("tr", null, /* @__PURE__ */ React.createElement("td", { colSpan: 2, "aria-label": "Spinner" }, /* @__PURE__ */ React.createElement(Spinner, { text: "Loading investigations ..." }))) : /* @__PURE__ */ React.createElement(ListRows, { investigations })))
  ), !!selectedInvOptions.length && /* @__PURE__ */ React.createElement(SelectedInvestigationsContainer, null, /* @__PURE__ */ React.createElement(ControlLabel, null, "Select an investigation to open in drawer"), /* @__PURE__ */ React.createElement(SelectedInvestigationsColumn, null, selectedInvOptions.map((inv) => /* @__PURE__ */ React.createElement("span", { key: inv.value }, inv.label)))), /* @__PURE__ */ React.createElement(Modal, { show: showConfirmationDialog, onHide: () => addEvidenceToSelectedInvestigations("cancel"), isConfirmDialog: true }, /* @__PURE__ */ React.createElement(Modal.Header, null, /* @__PURE__ */ React.createElement(Modal.Title, null, "Relative search as evidence")), /* @__PURE__ */ React.createElement(Modal.Body, null, /* @__PURE__ */ React.createElement("div", null, "The search you are trying to add is relative to the current time. This means that the results will change.", /* @__PURE__ */ React.createElement("br", null), /* @__PURE__ */ React.createElement("br", null), "Would you like to make an absolute copy of this search and add it to the investigation?")), /* @__PURE__ */ React.createElement(Modal.Footer, null, buttons)));
};
export default React.forwardRef(List);
